import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { t } from "i18next";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the current locale based on the selected language
 * @returns Locale string for current language
 */
export function getCurrentLocale(): string {
  const language = localStorage.getItem('i18nextLng') || 'en';
  
  // Map languages to locales
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'es': 'es-ES',
    'fr': 'fr-FR'
  };
  
  return localeMap[language] || 'en-US';
}

/**
 * Format currency values with localization support
 * @param value Number to format as currency
 * @param currency Currency code (default: USD)
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currency: string = "USD"): string {
  const locale = getCurrentLocale();
  
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value);
}

/**
 * Format number with commas and localization support
 * @param value Number to format
 * @param decimals Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export function formatNumber(value: number, decimals: number = 0): string {
  const locale = getCurrentLocale();
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format percentage values with localization support
 * @param value Number to format as percentage
 * @param decimals Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercent(value: number, decimals: number = 1): string {
  const locale = getCurrentLocale();
  
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
}

/**
 * Calculate Net Present Value (NPV)
 * @param initialInvestment Initial investment (negative cash flow)
 * @param cashFlows Array of future cash flows
 * @param discountRate Discount rate (e.g., 0.05 for 5%)
 * @returns Net Present Value
 */
export function calculateNPV(
  initialInvestment: number,
  cashFlows: number[],
  discountRate: number
): number {
  const adjustedRate = discountRate / 100;
  let npv = initialInvestment; // Initial investment is already negative
  
  for (let i = 0; i < cashFlows.length; i++) {
    npv += cashFlows[i] / Math.pow(1 + adjustedRate, i + 1);
  }
  
  return npv;
}

/**
 * Calculate simple payback period
 * @param initialCost Initial cost
 * @param annualSavings Annual savings
 * @returns Payback period in years
 */
export function calculatePayback(initialCost: number, annualSavings: number): number {
  if (annualSavings <= 0) return Infinity;
  return initialCost / annualSavings;
}

/**
 * Truncate text with ellipsis
 * @param text Text to truncate
 * @param maxLength Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Generate a random hex color
 * @returns Random hex color
 */
export function randomColor(): string {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
}

/**
 * Convert date to localized string based on current language
 * @param date Date to format
 * @param format Format option: 'short' (default), 'long', 'numeric', or 'relative'
 * @returns Formatted date string
 */
export function formatDate(date: Date | string, format: 'short' | 'long' | 'numeric' | 'relative' = 'short'): string {
  const locale = getCurrentLocale();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (format === 'relative') {
    return formatRelativeTime(dateObj);
  }
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: format === 'numeric' ? 'numeric' : format === 'long' ? 'long' : 'short',
    day: 'numeric',
  };
  
  if (format === 'long') {
    options.weekday = 'long';
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  return dateObj.toLocaleDateString(locale, options);
}

/**
 * Format time based on current language
 * @param date Date to format time from
 * @returns Formatted time string
 */
export function formatTime(date: Date | string): string {
  const locale = getCurrentLocale();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  return dateObj.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format a date as a relative time (e.g., "2 days ago")
 * @param date Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffMonth / 12);
  
  // Use i18next for translations
  if (diffYear > 0) {
    return t("dates.yearsAgo", { count: diffYear });
  } else if (diffMonth > 0) {
    return t("dates.monthsAgo", { count: diffMonth });
  } else if (diffDay > 0) {
    return t("dates.daysAgo", { count: diffDay });
  } else if (diffHour > 0) {
    return t("dates.hoursAgo", { count: diffHour });
  } else if (diffMin > 0) {
    return t("dates.minutesAgo", { count: diffMin });
  } else {
    return t("dates.justNow");
  }
}

/**
 * Get initials from a name
 * @param name Full name
 * @returns Initials (up to 2 characters)
 */
export function getInitials(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const names = trimmed.split(/\s+/);
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
}

/**
 * Helper function to translate validation error messages
 * @param message Original error message
 * @returns Translated error message
 */
export function translateErrorMessage(message: string): string {
  // Map common error messages to their translation keys
  if (message === "This field is required") {
    return t("errors.required");
  }
  
  if (message.includes("Invalid email")) {
    return t("errors.invalidEmail");
  }
  
  if (message.includes("Passwords don't match") || message.includes("Passwords do not match")) {
    return t("errors.passwordMismatch");
  }
  
  if (message.includes("must be at least")) {
    const minValue = message.match(/\d+/)?.[0];
    if (minValue) {
      return t("errors.minLength", { min: minValue });
    }
  }
  
  if (message.includes("cannot be more than") || message.includes("cannot exceed")) {
    const maxValue = message.match(/\d+/)?.[0];
    if (maxValue) {
      return t("errors.maxLength", { max: maxValue });
    }
  }
  
  if (message.includes("file size exceeds")) {
    return t("errors.fileSize");
  }
  
  if (message.includes("PDF files are allowed") || message.includes("Invalid file type")) {
    return t("errors.fileType");
  }
  
  if (message.includes("positive number")) {
    return t("errors.positiveNumber");
  }
  
  if (message.includes("value must be between")) {
    const matches = message.match(/between\s+(\d+)\s+and\s+(\d+)/);
    if (matches && matches.length >= 3) {
      return t("errors.numberRange", { min: matches[1], max: matches[2] });
    }
  }
  
  // For other messages without a direct translation, return the original
  return message;
}
